<template>
  <v-row no-gutters>
    <v-col class="wrap" cols="12">
      <div class="map" ref="map"></div>
      <v-overlay absolute :value="loading" :opacity="0.3" :z-index="4">
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
      </v-overlay>
    </v-col>
  </v-row>
</template>

<script>
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";
import { getCenter } from "geolib";
import {
  escapeHtml,
  formatCount,
  formatDistance,
  formatTime,
} from "../utils/common";
import { swrGet } from "../utils/swrCache";
import { reportGeocoderError } from "../utils/reportError";
import {
  LEADERBOARD_PAGE_SIZE,
  DEFAULT_LEADERBOARD_SORT,
} from "../utils/constants";
import { mapState, mapActions } from "vuex";

const { google } = window;

export const DEFAULT_COORDS = {
  lat: 1.3521,
  lng: 103.8198,
};

// Max rendered width of a hover tooltip. Drives both the InfoWindow's maxWidth
// and the horizontal edge-clamp in getTooltipPixelOffset (which shifts the
// centred box by TOOLTIP_WIDTH / 2). Keep these derived from one value.
const TOOLTIP_WIDTH = 250;
// Estimated rendered height of a tooltip; used to decide whether it fits above
// the pin. Content-dependent (taller for cluster photo collages), so it's a
// deliberate estimate rather than a measured value.
const TOOLTIP_HEIGHT = 170;

// Tooltip backgrounds. WITH_IMAGE is a translucent veil layered over the
// cleanup photo(s) so the text stays readable; WITHOUT_IMAGE is the solid green
// fallback used when there's no photo.
const TOOLTIP_GRADIENT_WITH_IMAGE =
  "linear-gradient(rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.6))";
// const TOOLTIP_GRADIENT_WITH_IMAGE = 'linear-gradient(rgba(255, 255, 255, 0.7), rgba(181, 217, 130, 0.7))';
// Alternative all-green veil: linear-gradient(rgba(151, 202, 81, 0.7), rgba(151, 202, 81, 0.7))
const TOOLTIP_GRADIENT_WITHOUT_IMAGE =
  "linear-gradient(rgba(233, 255, 74, 1), rgba(151, 202, 81, 1))";

// All-green alternative (flat brand colour, no heat scale):
// const CLUSTER_PALETTE = {
//   stops: ["#00C727", "#00C727", "#00C727", "#00C727"],
//   minSize: 26,
//   maxSize: 48,
//   maxCount: 10000,
// };

const CLUSTER_PALETTE = {
  // Colour stops, spread evenly across the log-count range (see
  // getPaletteColour): first = smallest counts, last = the ceiling.
  // stops: [
  //   "#c3ee95", // green  (smallest clusters — current brand colour)
  //   "#dff15a", // yellow-green
  //   "#ffe800", // yellow
  //   "#ff836c", // red    (busiest clusters)
  // ],

  // less saturated versions
  stops: [
    "#c6ef9b", // green  (smallest clusters — current brand colour)
    "#e1f06f", // yellow-green
    "#fcec3b", // yellow
    "#ff8d79", // red    (busiest clusters)
  ],
  minSize: 24, // cluster bubble diameter (px) at count = 1
  maxSize: 42, // cluster bubble diameter (px) at count >= maxCount
  maxCount: 10000, // ceiling: counts at or above this get max colour + size
};

// Option B — Stridy brand palette (teal → yellow-green)
// const CLUSTER_PALETTE = { stops: ['#26A69A','#66BB6A','#C6EE8E','#DFF15A'], minSize:36, maxSize:58, maxCount:10000 };

// Option C — Heatmap (light blue → deep purple)
// const CLUSTER_PALETTE = { stops: ['#90CAF9','#7986CB','#5C6BC0','#4527A0'], minSize:36, maxSize:58, maxCount:10000 };

// Make an API-sourced URL safe to embed in the tooltips' inline
// style="... url('...')" contexts: percent-encode exactly the characters that
// could break out of the quoted CSS/attribute value. A targeted map (rather
// than encodeURI) avoids double-encoding already-encoded URLs — note ' is
// "unreserved", so even encodeURIComponent would leave it through.
const URL_ESCAPES = {
  "'": "%27",
  '"': "%22",
  "(": "%28",
  ")": "%29",
  "<": "%3C",
  ">": "%3E",
  "\\": "%5C",
  " ": "%20",
};
const escapeUrl = (url) =>
  String(url == null ? "" : url).replace(/['"()<>\\ ]/g, (c) => URL_ESCAPES[c]);

export const hexToRgb = (hex) => {
  const n = parseInt(hex.replace("#", ""), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

// Colour is normalised against a `ceiling` count rather than raw counts, so the
// palette's reddest stop maps to `ceiling` and count = 1 stays greenest.
//
// The renderer passes the largest cluster count currently on the map as the
// ceiling (see stats.clusters.markers.max below), so colouring re-bases on every
// zoom/pan: the biggest visible cluster is always reddest, count = 1 always
// greenest, others interpolated logarithmically between. Omitting `ceiling`
// falls back to the fixed CLUSTER_PALETTE.maxCount (absolute scale).
//
// Alternative colour scales considered (kept for reference):
//   • Full stretch: also pull the FLOOR down to the smallest visible count, so
//     the smallest visible cluster is greenest too (t = (log c - log min) /
//     (log max - log min)) — max contrast, but small clusters never look small.
//   • Linear variant of the above (no Math.log) — exaggerates close counts.
export const getPaletteColour = (count, ceiling = CLUSTER_PALETTE.maxCount) => {
  const { stops } = CLUSTER_PALETTE;
  // Guard against a ceiling of 1 (log(1) = 0 → divide-by-zero); clusters are
  // always >= 2 in practice, but be safe.
  const cap = Math.max(2, ceiling);
  const clamped = Math.max(1, Math.min(count, cap));
  const t = Math.log(clamped) / Math.log(cap);

  // Find surrounding stops by mapping t into the stops array
  const lastIdx = stops.length - 1;
  const scaled = t * lastIdx;
  const lo = Math.floor(scaled);
  const hi = Math.min(lo + 1, lastIdx);
  const frac = scaled - lo;

  const a = hexToRgb(stops[lo]);
  const b = hexToRgb(stops[hi]);
  const r = Math.round(a.r + frac * (b.r - a.r));
  const g = Math.round(a.g + frac * (b.g - a.g));
  const bl = Math.round(a.b + frac * (b.b - a.b));
  return "#" + [r, g, bl].map((v) => v.toString(16).padStart(2, "0")).join("");
};

export const getPaletteSize = (count) => {
  const { minSize, maxSize, maxCount } = CLUSTER_PALETTE;
  const clamped = Math.max(1, Math.min(count, maxCount));
  const t = Math.log(clamped) / Math.log(maxCount);
  return Math.round(minSize + t * (maxSize - minSize));
};

// Icon geometry. The icon SVG is the original 58×53 box, which includes the
// teardrop plus the designer drop shadow (a blurred, gradient-filled blob to the
// lower-right). The §H crop to 40×50 (to stop optimized:false DOM markers from
// blocking neighbours) is no longer needed under optimized:true canvas rendering,
// so we use the full original art. CLUSTER_PALETTE sizes are already calibrated
// to this 58×53 box, so toIconSize is effectively identity. TIP_X/TIP_Y is the
// pin point (where the marker anchors to its lat/lng), in box coordinates.
const ICON_W = 58;
const ICON_H = 53;
const TIP_X = 21;
const TIP_Y = 50;

// Memoise encoded icons by (count, colour) — markerclusterer re-runs the
// renderer for every visible cluster on each zoom/pan, and base64-encoding the
// SVG each time is wasted work when the same count/colour recurs.
const _iconCache = new Map();

// --- Individual pin appearance knobs (all in the 58×53 viewBox coordinate
// space; tweak freely). These are module-level constants, so changing one and
// reloading rebuilds every pin.
const PIN_OUTLINE = true; // draw a black outline around the teardrop body
const PIN_OUTLINE_WIDTH = 0.8; // outline thickness
const PIN_HEAD_CX = 20.4; // white head circle centre x
const PIN_HEAD_CY = 19.7; // white head circle centre y
const PIN_HEAD_R = 11.5; // white head circle radius

// Teardrop pin. Used for INDIVIDUAL cleanups only (clusters use the circular
// bubble below), so it carries no number: a single cleanup is one event, and its
// head stays a blank white dot. Colour is the only thing that varies, and so the
// only thing keyed.
const getIcon = (colour) => {
  const cached = _iconCache.get(colour);
  if (cached) return cached;
  // Original 58×53 icon: a blurred gradient shadow blob (defs a/b), the coloured
  // teardrop and the white head. The shadow is baked into the SVG, so under
  // optimized:true it rasterises once per unique icon (getIcon is memoised) and
  // is drawn onto the canvas like any image.
  const encoded = window.btoa(`
    <svg width="${ICON_W}" height="${ICON_H}" viewBox="0 0 ${ICON_W} ${ICON_H}" xmlns="http://www.w3.org/2000/svg">
        <defs>
            <linearGradient x1="65.11%" y1="21.76%" x2="3.317%" y2="86.232%" id="a">
                <stop stop-opacity="0" offset="0%"/>
                <stop stop-opacity=".194" offset="100%"/>
            </linearGradient>
            <filter x="-8.3%" y="-9.7%" width="116.5%" height="119.4%" filterUnits="objectBoundingBox" id="b">
                <feGaussianBlur stdDeviation="1" in="SourceGraphic"/>
            </filter>
        </defs>
        <g fill="none" fill-rule="evenodd">
            <!-- Drop shadow: blurred gradient blob offset to the lower-right, sitting behind the pin -->
            <path d="m46.574 19.562-.36-.112c-8.089-2.298-19.244.98-25.14 7.386-4.647 5.051-3.67 6.828-2.092 22.214l.057.55c22.667-7.809 26.48-8.443 31.325-13.709 2.881-3.131 4.101-6.612 3.392-9.676-.709-3.063-3.289-5.46-7.173-6.66l-.01.007z" fill="url(#a)" filter="url(#b)" transform="translate(.93)"/>
            <!-- Teardrop body: the coloured pin shape; outline toggled via PIN_OUTLINE -->
            <path d="M20.462.013h-.48C9.385.273.931 8.925.93 19.51c0 8.345 2.713 10.242 19.051 29.405l.585.685C36.646 30.542 40 28.208 40 19.51a19.497 19.497 0 0 0-5.722-13.796A19.548 19.548 0 0 0 20.465 0l-.003.013z" fill="${colour}"${
    PIN_OUTLINE ? ` stroke="#163223" stroke-width="${PIN_OUTLINE_WIDTH}"` : ""
  }/>
            <!-- White head: the dot in the pin's centre; size/position via PIN_HEAD_* -->
            <circle cx="${PIN_HEAD_CX}" cy="${PIN_HEAD_CY}" r="${PIN_HEAD_R}" fill="#FFF"/>
        </g>
    </svg>
  `);
  _iconCache.set(colour, encoded);
  return encoded;
};

// Cluster bubble. A solid filled circle with the white count in the centre —
// visually distinct from the teardrop pin so "group of cleanups" (circle) reads
// differently from "one cleanup" (pin). Drawn in a fixed 100×100 viewBox and
// scaled via the marker's scaledSize, so the text stays crisp at any diameter.
// The thin white ring keeps overlapping bubbles legible. Memoised like getIcon.
const _clusterIconCache = new Map();

// Memoise cluster aggregation by the cluster's markers array. Hover, click
// and repeat clicks of the same rendered cluster share one computation; the
// WeakMap frees entries when the clusterer rebuilds its clusters.
const _clusterStatsCache = new WeakMap();
const getClusterIcon = (count, colour, diameter) => {
  const cacheKey = `${count}|${colour}|${diameter}`;
  const cached = _clusterIconCache.get(cacheKey);
  if (cached) return cached;
  // The SVG (100×100 viewBox) is scaled to the bubble's displayed diameter, so a
  // fixed font-size would render smaller for small counts. Counteract that
  // scaling: pick a target displayed text height (px) and convert it back into
  // viewBox units so the number reads the same size at every bubble.
  const TARGET_TEXT_PX = 12;
  const fontSize = Math.round((TARGET_TEXT_PX * 100) / diameter);
  const encoded = window.btoa(`
    <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="46" fill="${colour}" stroke="#7d948a" stroke-width="2"/>
        <text x="50" y="50" dy=".35em" font-family="Avenir" font-size="${fontSize}" font-weight="600" text-anchor="middle" fill="#3c3c3c">${count}</text>
    </svg>
  `);
  _clusterIconCache.set(cacheKey, encoded);
  return encoded;
};

// Circular hit region centred on the pin's head, so the pin's own hover/click is
// confined to the visible head rather than the whole (shadow-padded) icon box.
// Coords are in the displayed (scaled) icon pixel space; tweak the radius to taste.
const getIconShape = ([w, h]) => ({
  type: "circle",
  coords: [
    Math.round((20.5 * w) / ICON_W), // centre x — pin head
    Math.round((19.5 * h) / ICON_H), // centre y — pin head
    Math.round((16 * w) / ICON_W), // radius   — ~the visible head
  ],
});

// Anchor a scaled icon at the pin tip (TIP_X/TIP_Y in box coords). Not simply
// (w/2, h): the box has shadow margin on the right/bottom, so the tip is no
// longer at the box's horizontal centre or bottom edge.
const iconAnchor = ([w, h]) =>
  new google.maps.Point(
    Math.round((w * TIP_X) / ICON_W),
    Math.round((h * TIP_Y) / ICON_H)
  );

// The individual (count = 1) marker is identical for every stride. Its size and
// hit-shape are pure (no google.maps), so they're computed once here; the icon
// object needs `google.maps.Size/Point`, so it's built lazily on first use (and
// then cached) rather than at module load — otherwise merely importing this file
// where Maps JS hasn't loaded (e.g. in unit tests) would throw. In the app, Maps
// JS loads synchronously before the bundle, so the first call is already safe.
// The individual pin's own size, tuned directly — no longer derived from
// getPaletteSize (which scales the cluster bubble by count). That coupling
// meant retuning CLUSTER_PALETTE's minSize/maxSize silently resized individual
// pins too, via an arbitrary "count = 10" sample point. This value matches
// that prior visual size, but is now independent and tunable on its own.
const PIN_SIZE_FACTOR = 0.6;
const SINGLE_SIZE = [
  Math.round(ICON_W * PIN_SIZE_FACTOR),
  Math.round(ICON_H * PIN_SIZE_FACTOR),
];
const SINGLE_ICON_SHAPE = getIconShape(SINGLE_SIZE);
let _singleIcon = null;
const getSingleIcon = () => {
  if (!_singleIcon) {
    _singleIcon = {
      // url: `data:image/svg+xml;base64,${getIcon(getPaletteColour(1))}`,
      url: `data:image/svg+xml;base64,${getIcon("#00C727")}`, // single cleanup always green now; clusters use circle icon
      scaledSize: new google.maps.Size(SINGLE_SIZE[0], SINGLE_SIZE[1]),
      anchor: iconAnchor(SINGLE_SIZE),
    };
  }
  return _singleIcon;
};

const ORIGINAL_ZOOM_SCALE = 2;
// How long a deferred marker rebuild waits for the map's `idle` event before
// running anyway. Rebuilds are deferred so their synchronous cost doesn't
// freeze an in-flight camera animation; the timeout covers camera commands
// that turn out to be no-ops (already at the target), which fire no `idle`
// at all and would otherwise leave the rebuild pending forever.
const MARKER_REBUILD_IDLE_TIMEOUT_MS = 1500;
// After an `idle`, how long the camera must stay still before a deferred
// marker rebuild runs. Long enough to bridge the seam between two chained
// camera moves (where a spurious `idle` fires), short enough not to delay the
// rebuild noticeably once the map has genuinely settled.
const MARKER_REBUILD_SETTLE_MS = 250;
// Fallback zoom used only when a country is filtered but we can't geocode its
// viewport to frame it — we then centre on the data centroid at this level.
const COUNTRY_FALLBACK_ZOOM_SCALE = 11;
// Zoom used when the crosshair locates the viewer but we can't resolve/frame
// their country (geocoding unavailable) — we just zoom to their point instead.
const USER_POINT_ZOOM_SCALE = 6;
// Fraction of the country's geocoded viewport span that fitBounds should frame.
// The raw viewport reads a touch too far out, so we fit a slightly smaller box
// around the same centre: < 1 zooms in tighter (0.9 = fit 90% of the span),
// 1 = the whole viewport, > 1 adds margin. Applied to the bounds directly so
// fitBounds lands at the target zoom in one step — no post-fit adjust/flicker.
const COUNTRY_FIT_SPAN_FACTOR = 0.9;
// Ceiling on how far the team-fit (fitToData) zooms in. fitBounds alone would
// slam to max zoom when a team's pins are a single point or tightly grouped;
// this caps it so pins gain detail without the map diving to street level.
const TEAM_FIT_MAX_ZOOM_SCALE = 15;

export default {
  props: ["locations", "loading"],
  data() {
    return {
      activeInfoWindow: null,
      tooltipTimeout: null,
      map: null,
      overlay: null,
      cluster: null,
      // Per-country cache of the geocode Promise resolving to its viewport, so
      // the country-change render and the follow-up data-load render share one
      // in-flight geocode instead of firing two.
      countryViewportCache: {},
      geocoder: null,
      // Set when the country filter is cleared (had a value → null) so the next
      // render(s) keep the current pan/zoom instead of snapping back to the
      // global default view. Survives the follow-up locations() re-render (the
      // cleared filter triggers a refetch); reset once a real country is framed.
      preserveViewOnClear: false,
      // Pending deferred marker rebuild (see scheduleMarkerRebuild): the map
      // idle/bounds_changed listeners, the post-idle settle timer, and the
      // no-op-camera fallback timer.
      // Pending once-listener from fitToData's post-fitBounds zoom cap. Cancelled
      // before re-registering (and on destroy) so a fitBounds that never fires
      // idle can't leave a stale listener to snap the zoom on a later user move.
      fitIdleListener: null,
      rebuildIdleListener: null,
      rebuildMoveListener: null,
      rebuildSettleTimer: null,
      rebuildTimer: null,
      // True while a real camera command is still animating: set the moment a
      // camera mutation actually changes the view (see applyCamera), cleared on
      // the resulting `idle`. handleMap defers the (heavy, synchronous) marker
      // rebuild only while this is true — so a data-only refetch that reframes
      // the SAME viewport (e.g. clearing the time filter while a country stays
      // selected) rebuilds immediately instead of stranding on the idle-timeout
      // fallback. The once-listener that clears it.
      cameraBusy: false,
      cameraIdleListener: null,
    };
  },
  mounted() {
    this.handleMap();
    this.prefetchUserCountryData();
  },
  computed: {
    ...mapState(["country", "countries", "team", "superteam"]),
  },
  watch: {
    // Rebuild on a real change to either input. `country` (Vuex) changes from the
    // filter bar; `locations` gets a fresh array reference from Home on every
    // fetch. Watching them directly avoids the old O(n) `[...].join()` key (which
    // also missed same-length content changes) and spurious re-renders.
    country(newCountry, oldCountry) {
      // Clearing the filter (a country → Worldwide) should leave the map where
      // it is rather than resetting to the global default.
      if (!newCountry && oldCountry) this.preserveViewOnClear = true;
      // Camera only: at this point `locations` still holds the outgoing
      // dataset (the refetch hasn't landed), so rebuilding markers here would
      // do the most expensive possible rebuild just to throw it away moments
      // later — and freeze the camera animation while at it. The `locations`
      // watcher does the real rebuild when the new data arrives. fitCamera is
      // async (it may geocode); swallow any rejection so it can't surface as an
      // unhandled promise rejection (Sentry noise).
      this.fitCamera().catch(() => {});
    },
    locations() {
      this.handleMap();
    },
  },
  methods: {
    ...mapActions(["updateSelection", "updateCountry", "showSnackbar"]),
    // Shared hover-tooltip wiring. Opens an InfoWindow with `getContent()` on
    // mouseover, positioned via `getTooltipPixelOffset(getLatLng(), height)`,
    // and closes it (after a short grace period) on mouseout. Used for both the
    // individual location markers and the multi-marker cluster markers.
    bindHoverTooltip(
      marker,
      { getContent, getLatLng, tooltipHeight, iconHeight }
    ) {
      let infoWindow = null;

      marker.addListener("mouseover", () => {
        clearTimeout(this.tooltipTimeout);
        if (!infoWindow) {
          infoWindow = new google.maps.InfoWindow({
            content: getContent(),
            disableAutoPan: true,
            maxWidth: TOOLTIP_WIDTH,
            maxHeight: tooltipHeight,
          });
        }
        if (this.activeInfoWindow) {
          this.activeInfoWindow.close();
        }
        infoWindow.setOptions({
          pixelOffset: this.getTooltipPixelOffset(
            getLatLng(),
            tooltipHeight,
            iconHeight
          ),
        });
        infoWindow.open(this.map, marker);
        this.activeInfoWindow = infoWindow;

        // marker.setAnimation(google.maps.Animation.BOUNCE);
        // setTimeout(() => marker.setAnimation(null), 750);
      });

      marker.addListener("mouseout", () => {
        clearTimeout(this.tooltipTimeout);
        this.tooltipTimeout = setTimeout(() => {
          if (infoWindow && this.activeInfoWindow === infoWindow) {
            infoWindow.close();
            this.activeInfoWindow = null;
          }
        }, 200);
      });
    },
    // Wire the hover tooltip (and click-to-zoom) onto an individual location
    // marker. markerclusterer v1.0.27 renders singletons as the original marker,
    // so this keeps the tooltip working at every zoom level — clustered or not.
    attachTooltip(marker, position) {
      this.bindHoverTooltip(marker, {
        getContent: () => this.getTooltipContent(position),
        getLatLng: () =>
          new google.maps.LatLng(position.latitude, position.longitude),
        tooltipHeight: TOOLTIP_HEIGHT,
        iconHeight: SINGLE_SIZE[1],
      });

      marker.addListener("click", () => {
        // Show this single cleanup's stats in the side panel.
        this.selectCleanup(position);
        // Pan to the clicked cleanup so it stays in view.
        this.map.panTo(marker.getPosition());
        // Zoom-in on click is disabled for now; uncomment to restore it.
        // this.map.setZoom(15);
      });
    },
    // Build the side-panel selection for a single clicked cleanup. Strides carry
    // item COUNT, distance and duration, so those are set here; weight and the
    // per-item-type breakdown aren't on the stride record, so Home fetches them
    // from the selection breakdown endpoint off the strideIds set here.
    selectCleanup(position) {
      this.updateSelection({
        type: "cleanup",
        count: 1,
        items: position.items_count || 0,
        durationMinutes: position.duration_in_minutes || 0,
        distanceMetres: position.distance || 0,
        people: 1,
        countries: position.country ? 1 : 0,
        strideIds: position.id != null ? [position.id] : [],
      });
    },
    // Build the side-panel selection for a clicked cluster, aggregated over its
    // member strides.
    selectCluster(markers) {
      const stats = this.getClusterStats(markers);
      this.updateSelection({
        type: "cluster",
        count: stats.count,
        items: stats.items,
        durationMinutes: stats.durationMinutes,
        distanceMetres: stats.distanceMetres,
        people: stats.people,
        countries: stats.countries,
        strideIds: stats.strideIds,
      });
    },
    // Wire the hover tooltip onto a multi-marker cluster marker, showing the
    // combined totals across its member markers. No click handler — the cluster
    // keeps markerclusterer's default click-to-zoom-to-bounds behaviour.
    attachClusterTooltip(marker, latLng, markers, iconHeight) {
      this.bindHoverTooltip(marker, {
        // Aggregate lazily, only when the cluster is actually hovered.
        // `bindHoverTooltip` calls getContent once (first hover) and caches the
        // InfoWindow, so this never re-runs. Computing stats eagerly in the
        // renderer instead cost O(n) across all clusters on every zoom/pan.
        getContent: () =>
          this.getClusterTooltipContent(this.getClusterStats(markers)),
        getLatLng: () => latLng,
        tooltipHeight: TOOLTIP_HEIGHT,
        iconHeight,
      });
    },
    // Aggregate the requested totals over a cluster's member markers. Each
    // marker carries its source stride on `marker.locationData` (set in
    // handleMap). "People" counts distinct user_name values — strides carry no
    // team identifier, so unique participants is the closest available measure.
    getClusterStats(markers) {
      const cached = _clusterStatsCache.get(markers);
      if (cached) return cached;
      const data = markers.map((m) => m.locationData).filter(Boolean);
      const people = new Set();
      const countries = new Set();
      const strideIds = [];
      const images = [];
      let items = 0;
      let durationMinutes = 0;
      let distanceMetres = 0;
      data.forEach((d) => {
        items += d.items_count || 0;
        durationMinutes += d.duration_in_minutes || 0;
        distanceMetres += d.distance || 0;
        if (d.user_name) people.add(d.user_name);
        if (d.country) countries.add(d.country);
        if (d.id != null) strideIds.push(d.id);
        if (d.background_image) images.push(d.background_image);
      });
      const stats = {
        count: data.length,
        items,
        durationMinutes,
        distanceMetres,
        people: people.size,
        countries: countries.size,
        strideIds,
        images,
      };
      _clusterStatsCache.set(markers, stats);
      return stats;
    },
    // Keep the hover tooltip inside the visible map. The InfoWindow box renders
    // centred above its tip, so we flip it below the pin when there isn't room
    // above, and shift it horizontally by exactly the amount it would overflow
    // the left/right edge. Computed in real container pixels (not lat/lng
    // fractions) so it stays correct at every zoom level. Map stays put.
    getTooltipPixelOffset(
      latLng,
      tooltipHeight = TOOLTIP_HEIGHT,
      iconHeight = 0
    ) {
      const TOOLTIP_HALF = TOOLTIP_WIDTH / 2;
      const projection = this.overlay && this.overlay.getProjection();
      const mapDiv = this.$refs.map;
      if (!projection || !mapDiv) return new google.maps.Size(0, 0);

      // On-screen pixel position of the marker within the map container. The
      // pin is bottom-anchored, so this is the pin's base; its body rises
      // `iconHeight` px above it.
      const point = projection.fromLatLngToContainerPixel(latLng);
      const pixelX = point.x; // 0 = left edge of map
      const pixelY = point.y; // 0 = top edge of map (pin base)
      const mapWidth = mapDiv.offsetWidth;

      // The InfoWindow anchors its tip to the TOP of the pin icon, so its
      // natural position already sits `iconHeight` above the pin base. To drop
      // it cleanly below the pin we push down by the tooltip height *plus* the
      // icon height; otherwise taller pins (clusters) land too high. The room
      // available above the pin is likewise measured from the pin's top.
      const offsetY =
        pixelY - iconHeight < tooltipHeight ? tooltipHeight + iconHeight : 0;

      // Shift horizontally only by the overflow of the centred tooltip.
      let offsetX = 0;
      const leftEdge = pixelX - TOOLTIP_HALF;
      const rightEdge = pixelX + TOOLTIP_HALF;
      if (leftEdge < 0) offsetX = -leftEdge;
      else if (rightEdge > mapWidth) offsetX = mapWidth - rightEdge;

      return new google.maps.Size(Math.round(offsetX), offsetY);
    },
    getTooltipContent(position) {
      const backgroundImageStyle = position.background_image
        ? `background-image: ${TOOLTIP_GRADIENT_WITH_IMAGE}, url('${escapeUrl(
            position.background_image
          )}'); background-size: cover; background-position: center;`
        : `background: ${TOOLTIP_GRADIENT_WITHOUT_IMAGE};`;

      const photoClass = position.background_image ? " map-tooltip--photo" : "";

      return `
        <div class="map-tooltip${photoClass}" style="${backgroundImageStyle}">
          <div class="map-tooltip__content">
            <span class="map-tooltip__heading">${escapeHtml(
              position.user_name || "Unknown"
            )}</span>
            <span class="map-tooltip__value">
              ${new Date(position.finished_at).toLocaleDateString("en-US", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })} - ${escapeHtml(position.country || "Unknown")}
            </span>
            <div class="map-tooltip__row">
              <div class="map-tooltip__item">
                <span class="map-tooltip__value">${escapeHtml(
                  position.items_count || 0
                )}</span>
                <span class="map-tooltip__label">Items picked</span>
              </div>
              <div class="map-tooltip__item">
                <span class="map-tooltip__value">${formatTime(
                  position.duration_in_minutes || 0
                )}</span>
                <span class="map-tooltip__label">Duration</span>
              </div>
            </div>
            <div class="map-tooltip__row">
              <div class="map-tooltip__item">
                <span class="map-tooltip__value">${formatDistance(
                  position.distance
                )}</span>
                <span class="map-tooltip__label">Distance</span>
              </div>
            </div>
          </div>
        </div>
      `;
    },
    // Tooltip for a multi-marker cluster: combined totals across its members.
    getClusterTooltipContent(stats) {
      const distance = formatDistance(stats.distanceMetres);
      const items = formatCount(stats.items);

      // Time spent, broken down into years / months / days / hours.
      const duration = formatTime(stats.durationMinutes);

      // Background: a collage of up to 16 member cleanup photos (with a
      // translucent veil for readability). Falls back to the flat green gradient
      // when no member has a photo.
      const photos = stats.images.slice(0, 16);
      let background = `background: ${TOOLTIP_GRADIENT_WITHOUT_IMAGE};`;
      let collage = "";
      if (photos.length) {
        const cols =
          photos.length === 1
            ? 1
            : photos.length <= 2
            ? 2
            : photos.length === 3
            ? 3
            : photos.length <= 4
            ? 2
            : photos.length <= 9
            ? 3
            : 4;
        const cells = photos
          .map(
            (url) =>
              `<div class="map-tooltip__collage-cell" style="background-image: url('${escapeUrl(
                url
              )}');"></div>`
          )
          .join("");
        collage = `
          <div class="map-tooltip__collage" style="grid-template-columns: repeat(${cols}, 1fr);">${cells}</div>
          <div class="map-tooltip__veil" style="background: ${TOOLTIP_GRADIENT_WITH_IMAGE};"></div>
        `;
        background = "";
      }

      const photoClass = photos.length ? " map-tooltip--photo" : "";

      return `
        <div class="map-tooltip map-tooltip--cluster${photoClass}" style="${background}">
          ${collage}
          <div class="map-tooltip__content">
            <span class="map-tooltip__heading">${stats.count} cleanups</span>
            <span class="map-tooltip__value">Combined totals in this cluster</span>
            <div class="map-tooltip__row">
              <div class="map-tooltip__item">
                <span class="map-tooltip__value">${items}</span>
                <span class="map-tooltip__label">Items picked</span>
              </div>
              <div class="map-tooltip__item">
                <span class="map-tooltip__value">${duration}</span>
                <span class="map-tooltip__label">Time spent</span>
              </div>
            </div>
            <div class="map-tooltip__row">
              <div class="map-tooltip__item">
                <span class="map-tooltip__value">${distance}</span>
                <span class="map-tooltip__label">Distance</span>
              </div>
              <div class="map-tooltip__item">
                <span class="map-tooltip__value">${stats.people}</span>
                <span class="map-tooltip__label">People</span>
              </div>
            </div>
          </div>
        </div>
      `;
    },
    // Surface a user-facing message in the global snackbar (and mirror to the
    // console). Errors linger a little longer than the default toast.
    notify(message) {
      console.warn("[locate]", message);
      this.showSnackbar({ message, color: "error", timeout: 5000 });
    },
    // Lazily create (and memoise) the Geocoder. It's part of the already-loaded
    // Maps JS API, so no extra library or network setup is needed.
    getGeocoder() {
      if (!this.geocoder) this.geocoder = new google.maps.Geocoder();
      return this.geocoder;
    },
    // Promise resolving to a country's bounding viewport (a google.maps.LatLngBounds),
    // geocoded by name and cached per country so repeated renders reuse one
    // request. Resolves null if the country can't be geocoded.
    getCountryViewport(name) {
      if (!this.countryViewportCache[name]) {
        this.countryViewportCache[name] = new Promise((resolve) => {
          try {
            this.getGeocoder().geocode({ address: name }, (results, status) => {
              if (status !== "OK" || !results || !results.length) {
                resolve(null);
                return;
              }
              const country =
                results.find((r) => r.types.includes("country")) || results[0];
              resolve(country.geometry && country.geometry.viewport);
            });
          } catch (e) {
            // Geocoder unavailable (e.g. Geocoding API disabled on the key):
            // resolve null so callers fall back to fitting the data's bounds.
            // The fallback is correct and the map keeps working — but a
            // persistent failure here would otherwise be entirely invisible.
            reportGeocoderError(e, { operation: "countryViewport" });
            resolve(null);
          }
        });
      }
      return this.countryViewportCache[name];
    },
    // Scale a bounds around its centre by `scale`, so fitBounds frames that
    // fraction of the span: < 1 zooms in tighter, 1 = the same box, > 1 adds
    // margin. Returns a new LatLngBounds.
    scaleBounds(bounds, scale) {
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const center = bounds.getCenter();
      let lngSpan = ne.lng() - sw.lng();
      if (lngSpan < 0) lngSpan += 360; // bounds cross the antimeridian
      const halfLat = ((ne.lat() - sw.lat()) * scale) / 2;
      const halfLng = (lngSpan * scale) / 2;
      return new google.maps.LatLngBounds(
        { lat: center.lat() - halfLat, lng: center.lng() - halfLng },
        { lat: center.lat() + halfLat, lng: center.lng() + halfLng }
      );
    },
    // Frame the whole of `name` in the map. Uses the geocoded country viewport;
    // if that's unavailable, falls back to centring on the current data centroid
    // at COUNTRY_FALLBACK_ZOOM_SCALE. Guards against a stale result if the user
    // changed the filter while the geocode was in flight.
    async fitToCountry(name) {
      const viewport = await this.getCountryViewport(name);
      // The filter may have changed (or the map torn down) mid-request.
      if (!this.map || this.country !== name) return false;
      if (viewport) {
        return this.applyCamera(() =>
          this.map.fitBounds(
            this.scaleBounds(viewport, COUNTRY_FIT_SPAN_FACTOR)
          )
        );
      } else if (this.locations.length) {
        const centerBound = getCenter(this.locations);
        return this.applyCamera(() => {
          this.map.setCenter({
            lat: centerBound.latitude,
            lng: centerBound.longitude,
          });
          this.map.setZoom(COUNTRY_FALLBACK_ZOOM_SCALE);
        });
      }
      return false;
    },
    // Frame every current pin in view, zoomed in as far as they'll allow while
    // all stay visible. Used when a team is selected: the strides are scattered
    // arbitrarily (no country to geocode), so we fit the data's own bounds.
    // A max-zoom cap stops a single/tightly-grouped team from slamming to street
    // level. Returns whether the camera actually moved (false when there are no
    // locations to frame, or the fit reframes the same viewport).
    fitToData() {
      if (!this.map || !this.locations.length) return false;
      const bounds = new google.maps.LatLngBounds();
      this.locations.forEach((p) =>
        bounds.extend({ lat: p.latitude, lng: p.longitude })
      );
      // Cancel any zoom-cap listener from a previous call first: if that
      // fitBounds was a no-op, idle never fired and the listener would otherwise
      // survive to snap the zoom on the user's next manual pan/zoom.
      if (this.fitIdleListener) {
        google.maps.event.removeListener(this.fitIdleListener);
        this.fitIdleListener = null;
      }
      const moved = this.applyCamera(() =>
        this.map.fitBounds(this.scaleBounds(bounds, 1.2))
      );
      // Cap the zoom after fitBounds settles (fitBounds has no maxZoom option) —
      // only when it actually moved, since a no-op fires no idle to cap on.
      if (moved) {
        this.fitIdleListener = google.maps.event.addListenerOnce(
          this.map,
          "idle",
          () => {
            this.fitIdleListener = null;
            if (this.map && this.map.getZoom() > TEAM_FIT_MAX_ZOOM_SCALE) {
              this.map.setZoom(TEAM_FIT_MAX_ZOOM_SCALE);
            }
          }
        );
      }
      return moved;
    },
    // Resolve a geocoded country name to the matching filter option (so the
    // exact string in the dropdown is used), falling back to the geocoded name.
    resolveCountryName(name) {
      const match = this.countries.find(
        (c) => c.toLowerCase() === name.toLowerCase()
      );
      return match || name;
    },
    // Crosshair-button handler. Uses the standard browser Geolocation API only —
    // the coordinate stays in the browser and is used solely to reverse-geocode
    // the viewer's country; nothing is sent to any server. On success it seeds
    // the viewport cache and sets the country filter, which drives the map to
    // frame that country (see fitToCountry). Fails silently (keeping the current
    // view) if permission is denied, geolocation is unavailable, or the reverse
    // geocode finds no country.
    zoomToUserCountry() {
      if (!navigator.geolocation) {
        this.notify("Location isn't supported by this browser.");
        return;
      }
      this.locateWithRetry(2);
    },
    // getCurrentPosition can transiently fail with POSITION_UNAVAILABLE (macOS
    // kCLErrorLocationUnknown) or TIMEOUT — common on networks with weak
    // Wi-Fi-based geolocation, e.g. a phone hotspot. Retry a couple of times
    // before giving up; a genuine permission denial stops immediately.
    locateWithRetry(retries) {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => this.onLocated(coords),
        (err) => {
          const transient =
            err &&
            (err.code === err.POSITION_UNAVAILABLE || err.code === err.TIMEOUT);
          if (transient && retries > 0) {
            console.warn(
              `[locate] ${err.message} — retrying (${retries} attempt(s) left)…`
            );
            setTimeout(() => this.locateWithRetry(retries - 1), 1000);
            return;
          }
          if (err && err.code === err.PERMISSION_DENIED) {
            this.notify(
              "Location permission is blocked. Enable it for this site and try again."
            );
          } else {
            this.notify(
              "Couldn't determine your location. Your network (e.g. a phone hotspot) may not support location — try normal Wi-Fi."
            );
          }
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 600000 }
      );
    },
    // Reverse-geocode a lat/lng to its country. Resolves { ok: true, resolved,
    // viewport } — `resolved` matched against the filter dropdown's names — or
    // { ok: false, reason, status } when the point yields no country. Shared by
    // the crosshair flow (onLocated) and the startup prefetch.
    geocodeCountryAt(location) {
      return new Promise((resolve) => {
        try {
          this.getGeocoder().geocode({ location }, (results, status) => {
            if (status !== "OK" || !results || !results.length) {
              resolve({ ok: false, reason: "geocode-failed", status });
              return;
            }
            const country = results.find((r) => r.types.includes("country"));
            const comp =
              country &&
              country.address_components.find((c) =>
                c.types.includes("country")
              );
            if (!comp) {
              resolve({ ok: false, reason: "no-country" });
              return;
            }
            resolve({
              ok: true,
              resolved: this.resolveCountryName(comp.long_name),
              viewport: country.geometry && country.geometry.viewport,
            });
          });
        } catch (e) {
          // Geocoder unavailable — treat as a failed geocode (caller keeps the
          // current view / point zoom). Reported for the same reason as the
          // countryViewport path: the fallback hides a real outage.
          reportGeocoderError(e, { operation: "geocode" });
          resolve({ ok: false, reason: "geocode-failed" });
        }
      });
    },
    // Got a fix: move to the viewer's area immediately for visible feedback,
    // then reverse-geocode to refine to a whole-country fit + set the filter.
    async onLocated(coords) {
      const location = { lat: coords.latitude, lng: coords.longitude };
      this.map.panTo(location);
      this.map.setZoom(USER_POINT_ZOOM_SCALE);
      const res = await this.geocodeCountryAt(location);
      if (!res.ok) {
        this.notify(
          res.reason === "geocode-failed"
            ? `Zoomed to your location, but couldn't resolve your country (geocoder: ${res.status}).`
            : "Zoomed to your location, but couldn't determine your country."
        );
        return;
      }
      // Seed the cache so the follow-up fitToCountry is a cache hit and skips
      // a redundant forward geocode.
      this.countryViewportCache[res.resolved] = Promise.resolve(res.viewport);
      if (this.country === res.resolved) {
        // Already the active filter — the Vuex watcher won't refire, so frame
        // the country directly.
        this.fitToCountry(res.resolved);
      } else {
        this.updateCountry(res.resolved);
      }
    },
    // Warm the SWR cache for the viewer's country when geolocation permission
    // is ALREADY granted, so applying the country filter (crosshair or
    // dropdown) later renders instantly. The permissions API check guarantees
    // getCurrentPosition won't prompt; browsers without that API skip the
    // prefetch. Runs silently — any failure just leaves the cache cold. The
    // dashboard view itself stays on Worldwide.
    async prefetchUserCountryData() {
      if (!navigator.geolocation || !navigator.permissions) return;
      let permission;
      try {
        permission = await navigator.permissions.query({
          name: "geolocation",
        });
      } catch (e) {
        return;
      }
      if (permission.state !== "granted") return;
      navigator.geolocation.getCurrentPosition(
        async ({ coords }) => {
          const res = await this.geocodeCountryAt({
            lat: coords.latitude,
            lng: coords.longitude,
          });
          if (!res.ok) return;
          // Seed the viewport cache so a later fitToCountry skips a geocode.
          this.countryViewportCache[res.resolved] = Promise.resolve(
            res.viewport
          );
          this.prefetchCountry(res.resolved);
        },
        () => {}, // silent: this is a background warm-up, not a user action
        { timeout: 10000, maximumAge: 600000 }
      );
    },
    // Fire the requests the dashboard makes when `country` is selected with
    // otherwise-default filters, purely to warm the shared SWR cache. cacheKey
    // drops null params, so these keys match what Home and the Leaderboard
    // will ask for.
    prefetchCountry(country) {
      const params = { country };
      const swallow = (promise) => promise.catch(() => {});
      // Speculative: the user may never open this country. A failure costs
      // nothing and carries no user impact, so `silent` keeps it out of Sentry.
      // Note `silent` sits alongside `params`, NOT inside it — nested it would
      // be sent to the backend as a query parameter and never reach the
      // interceptor.
      swallow(swrGet("/dashboards/strides", { params, silent: true }).fresh);
      swallow(
        swrGet("/dashboards/strides_stats", { params, silent: true }).fresh
      );
      swallow(
        swrGet("/dashboards/leaderboard", {
          params: {
            country,
            offset: 0,
            limit: LEADERBOARD_PAGE_SIZE,
            tabItems: "individuals",
            // The order a freshly loaded panel asks for; another sort is a
            // different cache key, and only this one is worth warming.
            sortBy: DEFAULT_LEADERBOARD_SORT,
          },
          silent: true,
        }).fresh
      );
    },
    // Build the crosshair "locate me" button and dock it above Google's native
    // zoom controls (both live in the RIGHT_BOTTOM control stack).
    addLocateControl() {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "map-locate-btn";
      button.title = "Zoom to my country";
      button.setAttribute("aria-label", "Zoom to my country");
      button.innerHTML = '<span class="material-icons">my_location</span>';
      // Keep references so beforeDestroy can detach the listener and pull the
      // button back out of the map's control stack.
      this.locateButtonHandler = () => this.zoomToUserCountry();
      button.addEventListener("click", this.locateButtonHandler);
      this.locateButton = button;
      this.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(button);
    },
    async handleMap() {
      if (!this.map) {
        this.map = new google.maps.Map(this.$refs.map, {
          isFractionalZoomEnabled: true,
          zoom: ORIGINAL_ZOOM_SCALE,
          styles: [
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [{ visibility: "off" }],
            },
          ],
        });

        // Empty overlay solely to access the live map projection, which converts
        // lat/lng → on-screen container pixels correctly (handles world-wrap).
        this.overlay = new google.maps.OverlayView();
        this.overlay.onAdd = () => {};
        this.overlay.draw = () => {};
        this.overlay.onRemove = () => {};
        this.overlay.setMap(this.map);

        // Crosshair "locate me" control, docked above the native zoom buttons.
        this.addLocateControl();
      }

      await this.fitCamera();
      // fitCamera can await a network geocode; the component may have been
      // destroyed in the meantime (map torn down in beforeDestroy).
      if (!this.map) return;
      // Defer the rebuild only while the camera is genuinely mid-animation
      // (cameraBusy) — whether from this fitCamera call or one still settling
      // from the preceding `country` watcher. Rebuilding every marker is
      // synchronous and heavy enough to freeze the zoom mid-flight, so wait for
      // the map to settle first. When the camera isn't moving (a data-only
      // refetch that reframes the same viewport), rebuild immediately.
      if (this.cameraBusy) {
        this.scheduleMarkerRebuild();
      } else {
        this.cancelScheduledRebuild();
        this.rebuildMarkers();
      }
    },
    // Run a camera mutation and report whether it actually changed the view.
    // Google Maps applies center/zoom synchronously (only the visual transition
    // animates), so comparing the camera signature before and after detects a
    // no-op reframe — which fires no `idle` and would otherwise strand a
    // deferred marker rebuild on the MARKER_REBUILD_IDLE_TIMEOUT_MS fallback.
    // A real move marks the camera busy until its `idle` arrives.
    applyCamera(mutate) {
      const before = this.cameraSignature();
      mutate();
      const moved = this.cameraSignature() !== before;
      if (moved) this.markCameraBusy();
      return moved;
    },
    // A cheap serialization of the current camera target (centre + zoom). Google
    // Maps updates these synchronously on setCenter/setZoom/fitBounds, so a
    // before/after comparison tells whether a command actually moved the camera.
    cameraSignature() {
      const c = this.map && this.map.getCenter();
      return c ? `${c.lat()},${c.lng()},${this.map.getZoom()}` : "";
    },
    // Mark the camera as animating and arrange to clear the flag once it settles.
    // Re-registers the once-listener (dropping any prior one) so chained moves
    // stay "busy" until the final `idle`.
    markCameraBusy() {
      this.cameraBusy = true;
      if (this.cameraIdleListener) {
        google.maps.event.removeListener(this.cameraIdleListener);
      }
      this.cameraIdleListener = google.maps.event.addListenerOnce(
        this.map,
        "idle",
        () => {
          this.cameraIdleListener = null;
          this.cameraBusy = false;
        }
      );
    },
    // Camera half of a map update: frame the active filter (team pins, country
    // viewport, a preserved view after clearing, or the global default view).
    // Issues its camera commands via applyCamera, which sets `cameraBusy` when
    // one actually moves the view — that flag (not this return value) is what
    // handleMap uses to decide whether to defer the marker rebuild. Returns
    // whether this call moved the camera, for any caller that wants it.
    async fitCamera() {
      if (!this.map) return false;
      if (this.team || this.superteam) {
        // A team or superteam is selected: no country to geocode, so zoom to
        // fit the (aggregated, for a superteam) pins — all visible, in greater
        // detail. Framing a real filter, so the "just cleared" state is over.
        // fitToData no-ops (returns false) when there are no locations; report
        // that so the caller rebuilds markers immediately instead of waiting
        // on an idle that never comes.
        const moved = this.fitToData();
        this.preserveViewOnClear = false;
        return moved;
      }
      if (this.country) {
        // Frame the whole selected country (from the dropdown or the crosshair).
        const moved = await this.fitToCountry(this.country);
        // A real filter is being framed, so the "just cleared" state is over.
        this.preserveViewOnClear = false;
        return moved;
      }
      if (this.preserveViewOnClear) {
        // Filter was just cleared: keep the current pan/zoom (no reset).
        return false;
      }
      // No filter: the original zoomed-out global view. applyCamera reports "no
      // move" when the map is already parked there (a data refetch on the global
      // view), so the caller rebuilds immediately — a no-op camera command fires
      // no `idle`, and deferring would show pins ~MARKER_REBUILD_IDLE_TIMEOUT_MS
      // late.
      return this.applyCamera(() => {
        this.map.setCenter(DEFAULT_COORDS);
        this.map.setZoom(ORIGINAL_ZOOM_SCALE);
      });
    },
    // Run rebuildMarkers once the camera has settled. A single `idle` isn't
    // proof of that: the crosshair flow chains two camera moves (point-zoom,
    // then the country fitBounds) and an `idle` can fire in the seam between
    // them — rebuilding there freezes the second animation, the very jank this
    // defers. So after each `idle`, hold for MARKER_REBUILD_SETTLE_MS and let
    // any further movement (`bounds_changed`) push the rebuild to the next
    // `idle`. The MARKER_REBUILD_IDLE_TIMEOUT_MS fallback covers camera
    // commands that changed nothing (those fire no events at all); it's
    // cleared as soon as any camera event proves an `idle` is coming.
    // Replaces any rebuild already pending, so back-to-back updates only
    // rebuild once, from the latest data.
    scheduleMarkerRebuild() {
      this.cancelScheduledRebuild();
      const run = () => {
        this.cancelScheduledRebuild();
        this.rebuildMarkers();
      };
      const clearNoOpFallback = () => {
        if (this.rebuildTimer) {
          clearTimeout(this.rebuildTimer);
          this.rebuildTimer = null;
        }
      };
      this.rebuildIdleListener = this.map.addListener("idle", () => {
        clearNoOpFallback();
        clearTimeout(this.rebuildSettleTimer);
        this.rebuildSettleTimer = setTimeout(run, MARKER_REBUILD_SETTLE_MS);
      });
      this.rebuildMoveListener = this.map.addListener("bounds_changed", () => {
        clearNoOpFallback();
        clearTimeout(this.rebuildSettleTimer);
      });
      this.rebuildTimer = setTimeout(run, MARKER_REBUILD_IDLE_TIMEOUT_MS);
    },
    cancelScheduledRebuild() {
      if (this.rebuildIdleListener) {
        google.maps.event.removeListener(this.rebuildIdleListener);
        this.rebuildIdleListener = null;
      }
      if (this.rebuildMoveListener) {
        google.maps.event.removeListener(this.rebuildMoveListener);
        this.rebuildMoveListener = null;
      }
      if (this.rebuildTimer) {
        clearTimeout(this.rebuildTimer);
        this.rebuildTimer = null;
      }
      if (this.rebuildSettleTimer) {
        clearTimeout(this.rebuildSettleTimer);
        this.rebuildSettleTimer = null;
      }
    },
    // Marker half of a map update: tear down the clusterer's current markers
    // and rebuild them from `locations`.
    rebuildMarkers() {
      if (this.cluster) {
        this.cluster.clearMarkers();
        // Close any open tooltip before dropping the reference — clearing the
        // markers out from under an open InfoWindow orphans it on the map.
        if (this.activeInfoWindow) this.activeInfoWindow.close();
        clearTimeout(this.tooltipTimeout);
        this.activeInfoWindow = null;
      }

      // The previous selection (cluster/cleanup) refers to markers from the old
      // data set; drop it whenever the map rebuilds from a fresh fetch/filter.
      this.updateSelection(null);

      const markers = this.locations.map((position) => {
        const marker = new google.maps.Marker({
          position: {
            lat: position.latitude,
            lng: position.longitude,
          },
          // Shared module-level icon/shape — identical for every single marker.
          icon: getSingleIcon(),
          // Shrink the hover/click region to the visible pin head.
          shape: SINGLE_ICON_SHAPE,
          // Use default optimized (canvas) rendering: markers draw onto shared
          // canvas tiles instead of one DOM element each, which is ~20-50x cheaper
          // to render at scale (the previous `optimized: false` was the dominant
          // cause of zoom lag). Hover still works fine — the earlier "tooltip
          // doesn't show" symptom was a positioning bug (world-wrap offset), not
          // dropped mouse events. See docs/sprints/2026-07-dashboard-revamp.md
          // → "Map tooltips … marker-rendering performance".
          optimized: true,
        });

        // Carry the source stride so the cluster renderer can aggregate totals
        // across a cluster's member markers.
        marker.locationData = position;

        this.attachTooltip(marker, position);

        return marker;
      });

      // Reuse the one clusterer across rebuilds: a fresh instance per render
      // would leave the old one (and its map listeners) attached to the map,
      // accumulating listeners over repeated filter changes. clearMarkers (in
      // the teardown above) empties it; here we just hand it the new set.
      if (this.cluster) {
        this.cluster.addMarkers(markers);
        return;
      }

      this.cluster = new MarkerClusterer({
        markers,
        map: this.map,
        algorithm: new SuperClusterAlgorithm({ radius: 80, maxZoom: 17 }),
        // Clicking a cluster loads its combined stats into the side panel.
        // Providing onClusterClick overrides markerclusterer's default
        // zoom-to-bounds — which would dissolve the very cluster just selected.
        // To restore that zoom later, uncomment the fitBounds block below.
        onClusterClick: (_event, cluster /*, map */) => {
          this.selectCluster(cluster.markers);
          // Default zoom-to-bounds behaviour (disabled while a click selects the
          // cluster for the side panel):
          // if (cluster.bounds) map.fitBounds(cluster.bounds);
        },
        renderer: {
          // Only multi-marker clusters reach the renderer — markerclusterer
          // v1.0.27 places single markers directly (the original marker, which
          // already has its tooltip). Clusters show the count and, on hover, a
          // tooltip with the combined totals across their member markers.
          render: ({ count, position, markers }, stats) => {
            // Colour relative to the largest cluster currently on the map, so the
            // reddest stop tracks the busiest visible cluster and re-bases on
            // every zoom/pan. Size stays on the absolute scale (getPaletteSize).
            const colour = getPaletteColour(count, stats.clusters.markers.max);
            // Circular bubble: square, sized from the palette width, anchored at
            // its centre (it marks a region, not a precise point like a pin).
            const d = getPaletteSize(count);
            const half = Math.round(d / 2);
            const displayCount = formatCount(count);
            const marker = new google.maps.Marker({
              position,
              icon: {
                url: `data:image/svg+xml;base64,${getClusterIcon(
                  displayCount,
                  colour,
                  d
                )}`,
                scaledSize: new google.maps.Size(d, d),
                anchor: new google.maps.Point(half, half),
              },
              // Hit region = the whole circle.
              shape: { type: "circle", coords: [half, half, half] },
              // title: `Cluster of ${count} markers`, //removing cos it blocks our InfoWindow tooltip
              zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
              // Default optimized (canvas) rendering — cheap at scale; hover works.
              optimized: true,
            });

            this.attachClusterTooltip(marker, position, markers, d);

            return marker;
          },
        },
      });
    },
  },
  beforeDestroy() {
    this.cancelScheduledRebuild();
    if (this.fitIdleListener) {
      google.maps.event.removeListener(this.fitIdleListener);
      this.fitIdleListener = null;
    }
    if (this.cameraIdleListener) {
      google.maps.event.removeListener(this.cameraIdleListener);
      this.cameraIdleListener = null;
    }
    if (this.activeInfoWindow) {
      this.activeInfoWindow.close();
    }
    clearTimeout(this.tooltipTimeout);

    // Tear down the Google Maps resources so a destroy/recreate of this
    // component doesn't leak the map instance, overlay, clusterer, or the
    // injected locate-control button and its click listener.
    if (this.cluster) {
      this.cluster.clearMarkers();
      this.cluster = null;
    }
    if (this.overlay) {
      this.overlay.setMap(null);
      this.overlay = null;
    }
    if (this.locateButton) {
      this.locateButton.removeEventListener("click", this.locateButtonHandler);
      if (this.map) {
        const controls =
          this.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM];
        const index = controls.getArray().indexOf(this.locateButton);
        if (index > -1) controls.removeAt(index);
      }
      this.locateButton = null;
      this.locateButtonHandler = null;
    }
    this.map = null;
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/mixins.scss";
.wrap {
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.map {
  // lvh (largest viewport height) rather than svh: svh excludes the mobile
  // browser toolbars, so the map rendered ~half the physical screen on iOS even
  // though it filled 70% in desktop/emulation (no toolbar there). lvh tracks the
  // full screen and is stable (no resize jank as the toolbar collapses, unlike
  // dvh). No effect on desktop, where there's no collapsing toolbar.
  //
  // Single-column (portrait): a shorter, roughly square map. Two-column: taller
  // (see the --map-two-col-height var below). The column width is a flat 2/3 of
  // the row (set in Home.vue), independent of this height.
  height: 45lvh;
  // box-shadow only — NOT `all`: a height transition would animate the map on
  // every single/two-column flip (breakpoint change, rotation), which reads as
  // jank. The height is snapped, not eased.
  transition: box-shadow 0.3s ease;

  @include two-col {
    // Controlled by Home.vue's measured map height via the --map-two-col-height
    // CSS var (MAP_TWO_COL_HEIGHT_FRACTION of the content height below the
    // header); 70lvh is the fallback when Home.vue isn't the parent.
    height: var(--map-two-col-height, 70lvh);
  }

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  }
}

/* Crosshair "locate me" control — matches Google's native control chrome.
   Injected into the map DOM via map.controls, so it needs ::v-deep to pierce
   scoped styling. */
::v-deep .map-locate-btn {
  width: 40px;
  height: 40px;
  margin: 0 10px 10px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  color: #666;

  .material-icons {
    font-size: 22px;
  }

  &:hover {
    color: #333;
  }
}

/* The tooltip is hover-only — nothing inside it is clickable (the close button is
   hidden below), so let every mouse event fall through to the map underneath.
   Without this, a wheel/trackpad zoom that happens to land on an open tooltip is
   swallowed by the InfoWindow and scrolls the page instead of zooming the map. */
::v-deep .gm-style .gm-style-iw-a,
::v-deep .gm-style .gm-style-iw,
::v-deep .gm-style .gm-style-iw-c,
::v-deep .gm-style .gm-style-iw-d,
::v-deep .gm-style .gm-style-iw-t,
::v-deep .map-tooltip {
  pointer-events: none;
}

/* Custom InfoWindow styling */
::v-deep .gm-style .gm-style-iw {
  background: transparent !important;
  padding: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  overflow: visible !important;
}

::v-deep .gm-style .gm-style-iw-d {
  overflow: hidden !important;
  padding: 0 !important;
  max-width: none !important;
}

::v-deep .gm-style .gm-style-iw-c {
  padding: 0 !important;
  background: transparent !important;
  border-radius: 0 !important;
  max-width: none !important;
}

/* Custom tooltip styling */
::v-deep .map-tooltip {
  border-radius: 8px;
  padding: 12px;
  font-family: "Avenir", sans-serif;
  min-width: 180px;
  color: black;
}

::v-deep .map-tooltip__content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
}

/* White outline on text over photo backgrounds, for legibility */
::v-deep .map-tooltip--photo .map-tooltip__heading,
::v-deep .map-tooltip--photo .map-tooltip__value,
::v-deep .map-tooltip--photo .map-tooltip__label {
  text-shadow: 1px 1px 3px white;
}

/* Cluster tooltip: photo collage background + translucent veil behind text */
::v-deep .map-tooltip--cluster {
  position: relative;
  overflow: hidden;
}

::v-deep .map-tooltip__collage {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: grid;
  grid-auto-rows: 1fr;
}

::v-deep .map-tooltip__collage-cell {
  background-size: cover;
  background-position: center;
}

::v-deep .map-tooltip__veil {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* background set inline from TOOLTIP_GRADIENT_WITH_IMAGE */
}

::v-deep .map-tooltip--cluster .map-tooltip__content {
  position: relative;
  z-index: 1;
}

::v-deep .map-tooltip__row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

::v-deep .map-tooltip__item {
  display: flex;
  flex-direction: column;
}

::v-deep .map-tooltip__label {
  font-weight: 400;
  color: #666;
  font-size: 11px;
}

::v-deep .map-tooltip__value {
  font-weight: 800;
  color: #333;
  font-size: 13px;
}

::v-deep .map-tooltip__heading {
  font-weight: 800;
  color: #222;
  margin-bottom: 4px;
}

/* Hide InfoWindow close button */
::v-deep .gm-style-iw-c button.gm-ui-hover-effect {
  display: none !important;
}

/* Hide the InfoWindow tail/arrow — tooltip can sit above or below the pin */
::v-deep .gm-style .gm-style-iw-tc,
::v-deep .gm-style .gm-style-iw-tc::after,
::v-deep .gm-style .gm-style-iw-t::after {
  display: none !important;
}
</style>
